import { AreaClosed, LinePath, Line } from "@visx/shape";
import { curveLinear } from "@visx/curve";
import { scaleLinear } from "@visx/scale";
import { withTooltip, TooltipWithBounds, defaultStyles } from "@visx/tooltip";
// @ts-ignore
import type { WithTooltipProvidedProps } from "@visx/tooltip";
import { localPoint } from "@visx/event";
import { PatternLines } from "@visx/pattern";
import { max, min } from "@visx/vendor/d3-array";
import {
  differenceInDays,
  differenceInMonths,
  differenceInYears,
  isValid as isValidDate,
  parseISO,
} from "date-fns";
import React, { useMemo, useCallback } from "react";
import "./TemporalValue.scss";
import { formatDate } from "./utils";

// Color palette (replacing @data-ui/theme allColors.indigo)
const colors = {
  indigo4: "#748ffc",
  indigo5: "#748ffc",
};

const tooltipStyles = {
  ...defaultStyles,
  background: "white",
  border: "1px solid #e2e8f0",
  color: "#1e293b",
  padding: "8px",
  borderRadius: "4px",
  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
};

interface DataPoint {
  index: number;
  value: number;
  originalValue: any;
  label: string;
}

type TooltipData = DataPoint;

const normalizeTemporalIncrementSegment = (r: any) => {
  const { increment } = r.v as { increment: string; offset?: number };
  const startOffset = (r.v as any).offset || 0;
  const start = parseISO(r.r.start);
  const end = parseISO(r.r.end);
  const diff = increment.includes("year")
    ? differenceInYears(end, start)
    : increment.includes("month")
      ? differenceInMonths(end, start)
      : differenceInDays(end, start);
  return [startOffset, startOffset + diff];
};

interface TemporalChartViewProps {
  ranges: any[];
  width?: number;
  height?: number;
  margin?: { top: number; bottom: number; left: number; right: number };
}

const TemporalChartViewInner = ({
  ranges,
  width = 260,
  height = 70,
  margin = { top: 30, bottom: 5, left: 5, right: 60 },
  showTooltip,
  hideTooltip,
  tooltipData,
  tooltipTop = 0,
  tooltipLeft = 0,
}: TemporalChartViewProps & WithTooltipProvidedProps<TooltipData>) => {
  // Process data from ranges
  const { data, isBoolean } = useMemo(() => {
    const dataPoints: DataPoint[] = [];
    let isBool = false;

    (ranges || []).forEach((r: any) => {
      if (r.v && r.v.increment) {
        const [startVal, endVal] = normalizeTemporalIncrementSegment(r);
        dataPoints.push(
          { index: dataPoints.length, value: startVal, originalValue: startVal, label: r.r.start },
          { index: dataPoints.length + 1, value: endVal, originalValue: endVal, label: r.r.end }
        );
      } else {
        let numValue: number;
        if (r.v === true) {
          numValue = 1;
          isBool = true;
        } else if (r.v === false) {
          numValue = 0;
          isBool = true;
        } else if (r.v === false || typeof r.v === "undefined" || r.v === "Not Valid" || r.v === null) {
          numValue = 0;
        } else if (typeof r.v === "string" && isValidDate(parseISO(r.v))) {
          numValue = parseISO(r.v).valueOf();
        } else if (isNaN(Number.parseInt(r.v))) {
          numValue = 1;
        } else {
          numValue = 1;
        }
        dataPoints.push(
          { index: dataPoints.length, value: numValue, originalValue: r.v, label: r.r.start },
          { index: dataPoints.length + 1, value: numValue, originalValue: r.v, label: r.r.end }
        );
      }
    });

    return { data: dataPoints, isBoolean: isBool };
  }, [ranges]);

  // Bounds
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  // Scales
  const xScale = useMemo(
    () =>
      scaleLinear({
        range: [margin.left, innerWidth + margin.left],
        domain: [0, Math.max(data.length - 1, 1)],
      }),
    [data.length, innerWidth, margin.left]
  );

  const yScale = useMemo(() => {
    const minVal = min(data, (d: DataPoint) => d.value) ?? 0;
    const maxVal = max(data, (d: DataPoint) => d.value) ?? 1;
    const padding = (maxVal - minVal) * 0.1 || 0.5;
    return scaleLinear({
      range: [innerHeight + margin.top, margin.top],
      domain: [minVal - padding, maxVal + padding],
      nice: true,
    });
  }, [data, innerHeight, margin.top]);

  // Accessors
  const getX = (d: DataPoint) => d.index;
  const getY = (d: DataPoint) => d.value;

  // Tooltip handler
  const handleTooltip = useCallback(
    (event: React.TouchEvent<SVGRectElement> | React.MouseEvent<SVGRectElement>) => {
      const { x } = localPoint(event) || { x: 0 };
      const x0 = xScale.invert(x);
      const index = Math.round(x0);
      const clampedIndex = Math.max(0, Math.min(data.length - 1, index));
      const d = data[clampedIndex];
      if (d) {
        showTooltip({
          tooltipData: d,
          tooltipLeft: xScale(d.index),
          tooltipTop: yScale(d.value),
        });
      }
    },
    [showTooltip, xScale, yScale, data]
  );

  const formatTooltipValue = (d: DataPoint) => {
    let value: any = d.originalValue;
    if (typeof value === "undefined") value = "Unknown";
    else if (value === null || value === "Not Valid") value = "Uncertain";
    else if (typeof value === "string" && isValidDate(parseISO(value)))
      value = formatDate(value);
    else if (
      value &&
      (typeof value === "number" ||
        ((value as any).includes && !(value as any).includes(":") && Number.parseFloat(value)))
    )
      value = Number.parseFloat(value);
    else if (isBoolean) value = d.value === 0 ? "False" : "True";
    else if ((value as any)?.toString) value = (value as any).toString();
    return value;
  };

  const formatTooltipLabel = (d: DataPoint) => {
    if (d.label && typeof d.label === "string" && isValidDate(parseISO(d.label))) {
      return formatDate(d.label);
    }
    return d.label;
  };

  const getPointStroke = (d: DataPoint) => {
    const v = d.originalValue;
    return v === null ? "var(--secondary)" : v === "Not Valid" ? "var(--dark)" : colors.indigo4;
  };

  const getPointFill = (d: DataPoint) => {
    const v = d.originalValue;
    return v === null ? "var(--secondary)" : v === "Not Valid" ? "var(--dark)" : "#fff";
  };

  if (width < 10 || data.length === 0) return null;

  return (
    <div data-temporal-value-part="chart-view-container" style={{ position: "relative" }}>
      <svg width={width} height={height} aria-label="Temporal Value">
        <PatternLines
          id="area_pattern"
          height={4}
          width={4}
          stroke={colors.indigo4}
          strokeWidth={1}
          orientation={["diagonal"]}
        />
        {/* Area under the line */}
        <AreaClosed<DataPoint>
          data={data}
          x={(d) => xScale(getX(d)) ?? 0}
          y={(d) => yScale(getY(d)) ?? 0}
          yScale={yScale}
          strokeWidth={0}
          fill="url(#area_pattern)"
          curve={curveLinear}
        />
        {/* Line */}
        <LinePath<DataPoint>
          data={data}
          x={(d) => xScale(getX(d)) ?? 0}
          y={(d) => yScale(getY(d)) ?? 0}
          stroke={colors.indigo5}
          strokeWidth={2}
          curve={curveLinear}
        />
        {/* Data points */}
        {data.map((d, i) => (
          <circle
            key={`point-${i}`}
            cx={xScale(getX(d))}
            cy={yScale(getY(d))}
            r={3}
            fill={getPointFill(d)}
            stroke={getPointStroke(d)}
            strokeWidth={1.5}
          />
        ))}
        {/* Invisible rect for capturing mouse events */}
        <rect
          x={margin.left}
          y={margin.top}
          width={innerWidth}
          height={innerHeight}
          fill="transparent"
          onTouchStart={handleTooltip}
          onTouchMove={handleTooltip}
          onMouseMove={handleTooltip}
          onMouseLeave={() => hideTooltip()}
        />
        {/* Tooltip indicator */}
        {tooltipData && (
          <>
            <Line
              from={{ x: tooltipLeft, y: margin.top }}
              to={{ x: tooltipLeft, y: innerHeight + margin.top }}
              stroke={colors.indigo5}
              strokeWidth={1}
              strokeDasharray="4 4"
              pointerEvents="none"
            />
            <circle
              cx={tooltipLeft}
              cy={tooltipTop}
              r={4}
              fill={colors.indigo5}
              stroke="white"
              strokeWidth={2}
              pointerEvents="none"
            />
          </>
        )}
      </svg>
      {/* Tooltip */}
      {tooltipData && (
        <TooltipWithBounds
          key={Math.random()}
          top={tooltipTop - 12}
          left={tooltipLeft + 12}
          style={tooltipStyles}
        >
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span style={{ fontSize: "0.75rem", fontWeight: 700 }}>
              {formatTooltipValue(tooltipData)}
            </span>
            <span style={{ fontSize: "0.75rem" }}>
              {formatTooltipLabel(tooltipData)}
            </span>
          </div>
        </TooltipWithBounds>
      )}
    </div>
  );
};

export const TemporalChartView = withTooltip<TemporalChartViewProps, TooltipData>(
  TemporalChartViewInner
);

export default TemporalChartView;
